import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  downloadShipmentDocuments,
  getLabelContext,
  getLabelCourierQuotes,
  generateShipmentLabel,
  searchOrdersForLabel,
} from '../../services/shipturtleApi';
import { showToast } from '../../utils/toast';
import { describeQuote, quoteQcKind, summarizeQuotesForTara } from '../../utils/prozoChannels';
import {
  packageErrors,
  partyErrors,
  positiveNumberError,
  prefixKeys,
} from '../../utils/shippingValidation';
import { useTaraTerminalFocus } from '../../utils/taraTerminalContext';
import RefreshRatesButton, { QuotesSkeleton } from './RefreshRatesButton';
import './ManageLabels.css';

/**
 * Shipment label generation — forward and reverse.
 *
 * Mirrors the courier console's five-step flow. Both legs run the same steps
 * against the same endpoint; only the prefill direction and two reverse-only
 * fields differ:
 *
 *   FORWARD  ship from the warehouse -> to the customer
 *   REVERSE  ship from the customer  -> to the warehouse
 *
 * The order's own addresses are never modified. What the operator sees is
 * pre-filled from the order, and whatever they submit is frozen onto that
 * shipment alone.
 */

const FORWARD = 'forward';
const REVERSE = 'reverse';

const PARTY_FIELDS = [
  'name',
  'phone',
  'email',
  'address_line_1',
  'address_line_2',
  'landmark',
  'city',
  'state',
  'pincode',
  'country',
  'gstin',
];

const emptyParty = () =>
  PARTY_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), { country: 'India' });

const STEPS = {
  [FORWARD]: [
    'Select Order',
    'Customer Details',
    'Pickup Details',
    'Order Details',
    'Package Details',
    'Select Courier',
    'Review',
  ],
  [REVERSE]: [
    'Select Order',
    'Pickup Details',
    'Drop Details',
    'Order Details',
    'Package Details',
    'Select Courier',
    'Review',
  ],
};

/**
 * Volumetric (dimensional) weight in grams — the same divisor the Orders tab
 * uses. Couriers bill on whichever is greater, actual or volumetric, so the
 * operator needs to see it before choosing a package size.
 */
const volumetricWeightGrams = (length, breadth, height) => {
  const [l, b, h] = [length, breadth, height].map((v) => parseFloat(v));
  if (![l, b, h].every((v) => Number.isFinite(v) && v > 0)) return 0;
  return (l * b * h * 1000) / 5000;
};

const formatGrams = (grams) => {
  if (!Number.isFinite(grams) || grams <= 0) return '0 g';
  const rounded = Number.isInteger(grams) ? grams : Number(grams.toFixed(2));
  return `${rounded} g`;
};

// On a reverse leg the customer is the *sender*, so the steps swap sides.
const CUSTOMER_SIDE = { [FORWARD]: 'ship_to', [REVERSE]: 'ship_from' };
const WAREHOUSE_SIDE = { [FORWARD]: 'ship_from', [REVERSE]: 'ship_to' };

function ManageLabels() {
  // A clone arrives as navigation state from the exception-labels screen. Read
  // once into a ref: it is a one-shot prefill, and leaving it in `location` would
  // re-apply it every time the effect below re-runs.
  const location = useLocation();
  const clonePayload = useRef(location.state?.cloneFrom || null);
  const [cloneSource, setCloneSource] = useState(location.state?.cloneFrom?.cloned_from || null);
  const [cloneNotice, setCloneNotice] = useState('');

  const [leg, setLeg] = useState(clonePayload.current?.type || FORWARD);
  const [step, setStep] = useState(0);

  // Searchable order dropdown. Nothing is pre-fetched — matches appear as the
  // operator types, the way the vendor picker on Add Product behaves.
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  // A clone already knows its order, so the picker step is skipped.
  const [order, setOrder] = useState(
    clonePayload.current?.order?.id ? clonePayload.current.order : null
  );
  const searchWrapRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const [context, setContext] = useState(null);
  const [loadingContext, setLoadingContext] = useState(false);

  const [shipFrom, setShipFrom] = useState(emptyParty());
  const [shipTo, setShipTo] = useState(emptyParty());
  const [editedSides, setEditedSides] = useState({ ship_from: false, ship_to: false });
  // Errors show for a field once it is left (touched), or for the whole step
  // once Next is pressed on it (revealed) — never on a form not yet typed in.
  const [touched, setTouched] = useState({});
  const [revealed, setRevealed] = useState({});
  // What the server refused on the last submit, keyed like the client errors
  // (ship_from.phone, package.weight_g, …) — the server uses the same keys.
  const [serverErrors, setServerErrors] = useState({});
  const [warehouseId, setWarehouseId] = useState('');

  const [selection, setSelection] = useState({});
  const [chargeBearer, setChargeBearer] = useState('');
  const [returnReason, setReturnReason] = useState('');
  // Accounts-facing reason for this exception label. Mandatory here even though
  // the API accepts it as optional, so a hand-rolled request cannot be blocked
  // mid-dispatch but an operator never leaves it blank.
  const [internalNote, setInternalNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  const [qcType, setQcType] = useState('NONE');
  const [paymentMode, setPaymentMode] = useState('PREPAID');
  const [invoiceValue, setInvoiceValue] = useState('');

  const [pkg, setPkg] = useState({ weight_g: '', length_cm: '', breadth_cm: '', height_cm: '' });

  // Courier selection. Blank courier = let Prozo allocate, which is the default.
  const [courier, setCourier] = useState('');
  // The picked card. Several quotes can share one channel (a carrier's QC and
  // NOQC reverse services both resolve to it), so the channel cannot say which
  // card was picked.
  const [selectedQuoteId, setSelectedQuoteId] = useState('');
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quotesError, setQuotesError] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  // A reverse pickup only shows the quotes matching the QC type chosen in Order
  // Details. Quotes whose service code names neither QC nor NOQC stay visible.
  const visibleQuotes = useMemo(() => {
    if (leg !== REVERSE) return quotes;
    const wanted = qcType === 'DOORSTEP' ? 'QC' : 'NOQC';
    return quotes.filter((q) => {
      const kind = quoteQcKind(q?.provider?.service_type_primary);
      return kind === null || kind === wanted;
    });
  }, [quotes, leg, qcType]);

  // Changing the QC type after picking a card can hide it; drop the selection
  // rather than book a card the operator can no longer see.
  useEffect(() => {
    if (!selectedQuoteId) return;
    const stillVisible = visibleQuotes.some(
      (q, idx) => (q?.provider?.id || `idx-${idx}`) === selectedQuoteId,
    );
    if (!stillVisible) {
      setCourier('');
      setSelectedQuoteId('');
    }
  }, [visibleQuotes, selectedQuoteId]);

  useTaraTerminalFocus('label_wizard', {
    leg: leg === REVERSE ? 'reverse (return pickup)' : 'forward',
    step: STEPS[leg]?.[step],
    order_number: order?.order_number,
    package: pkg,
    selected_courier: courier || undefined,
    courier_quotes: summarizeQuotesForTara(visibleQuotes),
    created_awb: created?.awb_number,
  });
  const [downloadingDocs, setDownloadingDocs] = useState(false);

  const steps = STEPS[leg];

  const resetForOrder = useCallback(() => {
    setStep(0);
    setSelection({});
    setPkg({ weight_g: '', length_cm: '', breadth_cm: '', height_cm: '' });
    setReturnReason('');
    setInternalNote('');
    setNoteTouched(false);
    setQcType('NONE');
    setEditedSides({ ship_from: false, ship_to: false });
    setCourier('');
    setSelectedQuoteId('');
    setQuotes([]);
    setQuotesError('');
    setCreated(null);
    setError('');
    setTouched({});
    setRevealed({});
    setServerErrors({});
  }, []);

  // Load everything the form needs whenever the order or the leg changes.
  useEffect(() => {
    if (!order?.id) return;
    let cancelled = false;

    (async () => {
      setLoadingContext(true);
      setError('');
      try {
        const data = await getLabelContext(order.id, leg);
        if (cancelled) return;
        setContext(data);
        setShipFrom({ ...emptyParty(), ...(data.ship_from || {}) });
        setShipTo({ ...emptyParty(), ...(data.ship_to || {}) });
        // Deliberately NOT pre-filled from the order. This ships a *subset* of
        // lines, so the order's own total is the wrong number the moment fewer
        // than all items are selected — and a pre-filled box is easy to leave
        // untouched, silently declaring the full order value on a partial
        // shipment. Blank means "derive it from what is selected", which the
        // backend does via declared_total(); an override is a deliberate act.
        setInvoiceValue('');
        const preferred =
          (data.warehouses || []).find((w) => w.is_default) || (data.warehouses || [])[0];
        setWarehouseId(preferred?.id || '');
        // Forward defers to the vendor's billing rule; a return is exactly the
        // judgement call the operator is here to make, so it starts unset.
        setChargeBearer(leg === FORWARD ? 'SETTINGS' : '');

        // Clone prefill, applied *after* label-context so the source label's
        // quantities can be clamped against the order's ordered quantities.
        const clone = clonePayload.current;
        if (clone) {
          clonePayload.current = null;
          if (clone.ship_from) setShipFrom({ ...emptyParty(), ...clone.ship_from });
          if (clone.ship_to) setShipTo({ ...emptyParty(), ...clone.ship_to });
          if (clone.charge_bearer) setChargeBearer(clone.charge_bearer);
          if (clone.qc_type) setQcType(clone.qc_type);
          if (clone.return_reason) setReturnReason(clone.return_reason);
          if (clone.pickup_address_id) setWarehouseId(clone.pickup_address_id);
          if (clone.package) setPkg({ ...clone.package });
          // Both sides were edited on the source label, so record that rather
          // than letting the new one claim the addresses came straight from the
          // order.
          setEditedSides({ ship_from: true, ship_to: true });

          const remainingById = Object.fromEntries(
            (data.line_items || []).map((li) => [li.line_item_id, li.remaining_quantity])
          );
          const nextSelection = {};
          let clamped = false;
          let dropped = false;
          (clone.line_items || []).forEach((li) => {
            const remaining = remainingById[li.line_item_id];
            if (!remaining) { dropped = true; return; }
            const quantity = Math.min(li.quantity, remaining);
            if (quantity < li.quantity) clamped = true;
            nextSelection[li.line_item_id] = {
              checked: true,
              quantity,
              reason: li.return_reason || '',
            };
          });
          setSelection(nextSelection);

          if (dropped || clamped) {
            setCloneNotice(
              dropped
                ? 'Some items on the original label are no longer on this order and were dropped.'
                : 'Quantities were reduced to what was ordered.'
            );
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load this order');
      } finally {
        if (!cancelled) setLoadingContext(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [order?.id, leg]);

  // Close the dropdown when focus moves elsewhere on the page.
  useEffect(() => {
    const onClickAway = (event) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const runSearch = useCallback(async (term) => {
    // Two characters is the same threshold the vendor/category pickers use —
    // below it, a search matches most of the catalogue and is just noise.
    if (!term || term.trim().length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const data = await searchOrdersForLabel(term.trim());
      const rows = data?.orders || data?.results || (Array.isArray(data) ? data : []);
      setResults(rows);
    } catch (err) {
      setError(err.message || 'Search failed');
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleQueryChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    setShowDropdown(true);
    setError('');
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    // Debounced so a fast typist issues one request, not one per keystroke.
    searchTimeoutRef.current = setTimeout(() => runSearch(value), 300);
  };

  /** Wrap the matched substring so the operator can see *why* a row matched. */
  const highlightMatch = (text, term) => {
    const value = String(text ?? '');
    if (!value || !term || term.trim().length < 2) return value;
    const escaped = term.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = value.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, idx) =>
      part.toLowerCase() === term.trim().toLowerCase() ? <mark key={idx}>{part}</mark> : part
    );
  };

  const chooseOrder = (row) => {
    setOrder(row);
    setResults([]);
    setShowDropdown(false);
    setQuery(row.order_number || '');
    resetForOrder();
    // Straight on to the first detail step — selecting the order *is* step 1.
    setStep(1);
  };

  const updateParty = (side, field, value) => {
    const setter = side === 'ship_from' ? setShipFrom : setShipTo;
    setter((prev) => ({ ...prev, [field]: value }));
    setEditedSides((prev) => ({ ...prev, [side]: true }));
  };

  const chooseWarehouse = (id) => {
    setWarehouseId(id);
    const w = (context?.warehouses || []).find((x) => x.id === id);
    if (!w) return;
    const party = {
      ...emptyParty(),
      name: w.warehouse_name || w.contact_person || '',
      phone: w.contact_number || '',
      email: w.contact_email || '',
      gstin: w.gstin || '',
      address_line_1: w.address?.address_line_1 || '',
      address_line_2: w.address?.address_line_2 || '',
      landmark: w.address?.landmark || '',
      city: w.address?.city || '',
      state: w.address?.state || '',
      pincode: w.address?.pincode || '',
      country: 'India',
    };
    const side = WAREHOUSE_SIDE[leg];
    (side === 'ship_from' ? setShipFrom : setShipTo)(party);
    // Choosing a saved warehouse is not a manual edit — the backend records the
    // warehouse it came from.
    setEditedSides((prev) => ({ ...prev, [side]: false }));
  };

  // Memoised so the `selectedLines` dependency below is stable across renders.
  const lineItems = useMemo(() => context?.line_items || [], [context]);
  const selectedLines = useMemo(
    () =>
      lineItems
        .filter((li) => selection[li.line_item_id]?.checked && li.remaining_quantity > 0)
        .map((li) => ({
          line_item_id: li.line_item_id,
          quantity: Number(selection[li.line_item_id].quantity) || 0,
          ...(leg === REVERSE && selection[li.line_item_id].reason
            ? { return_reason: selection[li.line_item_id].reason }
            : {}),
        }))
        .filter((li) => li.quantity > 0),
    [lineItems, selection, leg]
  );

  /**
   * Declared value of the selected units, mirroring the backend.
   *
   * Shown because it is what the courier insures and, on a COD leg, what gets
   * collected — the operator should see it before creating the AWB rather than
   * discovering it on the label.
   */
  const declaredValue = useMemo(
    () =>
      selectedLines.reduce((total, sel) => {
        const li = lineItems.find((x) => x.line_item_id === sel.line_item_id);
        const unit = parseFloat(li?.price) || 0;
        return total + unit * (Number(sel.quantity) || 0);
      }, 0),
    [selectedLines, lineItems]
  );

  const toggleLine = (li, checked) => {
    setSelection((prev) => ({
      ...prev,
      [li.line_item_id]: {
        checked,
        quantity: prev[li.line_item_id]?.quantity ?? li.remaining_quantity,
        reason: prev[li.line_item_id]?.reason ?? '',
      },
    }));
  };

  const setLineField = (lineId, field, value) => {
    setSelection((prev) => ({ ...prev, [lineId]: { ...prev[lineId], [field]: value } }));
  };

  const toggleAll = (checked) => {
    const next = {};
    lineItems.forEach((li) => {
      if (li.remaining_quantity > 0) {
        next[li.line_item_id] = {
          checked,
          quantity: selection[li.line_item_id]?.quantity ?? li.remaining_quantity,
          reason: selection[li.line_item_id]?.reason ?? '',
        };
      }
    });
    setSelection(next);
  };

  // The rules in utils/shippingValidation.js mirror the server's, so a step
  // that passes here is not refused at Create label for the same field. The
  // server re-checks everything; this is for an instant, per-field message.
  //
  // Prozo needs from_email on a forward leg (the warehouse is the sender). On
  // a reverse leg the sender is the customer, who may have no email on file.
  const sidePartyErrors = (side) =>
    prefixKeys(
      partyErrors(side === 'ship_from' ? shipFrom : shipTo, {
        requireEmail: side === 'ship_from' && leg === FORWARD,
        who:
          side === CUSTOMER_SIDE[leg] ? 'Customer' : leg === FORWARD ? 'Pickup' : 'Drop',
      }),
      side
    );

  const stepErrors = (index) => {
    switch (index) {
      case 0:
        return order?.id && context ? {} : { order: 'Select an order to continue.' };
      case 1:
        return sidePartyErrors(CUSTOMER_SIDE[leg]);
      case 2:
        return sidePartyErrors(WAREHOUSE_SIDE[leg]);
      case 3: {
        const errors = {};
        if (selectedLines.length === 0) errors.lines = 'Select at least one item for this shipment.';
        if (!chargeBearer) errors.chargeBearer = 'Choose who bears the shipping charge.';
        if (leg === REVERSE && !returnReason.trim()) {
          errors.returnReason = 'An RVP reason is required for a reverse pickup.';
        }
        if (String(invoiceValue).trim()) {
          const invoiceError = positiveNumberError(invoiceValue, 'Invoice value');
          if (invoiceError) errors.invoice_value = invoiceError;
        }
        return errors;
      }
      case 4:
        return prefixKeys(packageErrors(pkg), 'package');
      case 5:
        // No recommendation rules on this flow — a courier must be chosen.
        return courier ? {} : { courier: 'Select a courier.' };
      default:
        return {};
    }
  };

  const stepValid = (index) => Object.keys(stepErrors(index)).length === 0;

  /** Which wizard step an error key lives on. */
  const stepForKey = (key) => {
    if (key.startsWith(`${CUSTOMER_SIDE[leg]}.`)) return 1;
    if (key.startsWith(`${WAREHOUSE_SIDE[leg]}.`)) return 2;
    if (['lines', 'chargeBearer', 'returnReason', 'invoice_value'].includes(key)) return 3;
    if (key.startsWith('package.')) return 4;
    if (key === 'courier') return 5;
    return 6;
  };

  const currentErrors = stepErrors(step);

  /** The message to show under a field, or null. */
  const fieldErr = (key) =>
    ((revealed[step] || touched[key]) && currentErrors[key]) || serverErrors[key] || null;
  const fieldClass = (key, extra = '') =>
    ['field', extra, fieldErr(key) ? 'fieldError' : ''].filter(Boolean).join(' ');
  const errorText = (key) => {
    const message = fieldErr(key);
    return message ? (
      <small className="fieldErrorText" role="alert">
        {message}
      </small>
    ) : null;
  };
  const touch = (key) => () => setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

  const goNext = () => {
    const count = Object.keys(currentErrors).length;
    if (count) {
      setRevealed((prev) => ({ ...prev, [step]: true }));
      showToast(`Fix ${count} ${count === 1 ? 'field' : 'fields'} before continuing.`, 'error');
      return;
    }
    // The operator has corrected this step; earlier server refusals for it
    // no longer describe what is on screen.
    setServerErrors((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([key]) => stepForKey(key) !== step))
    );
    setStep((prev) => prev + 1);
  };

  const canSubmit = internalNote.trim().length > 0 && !submitting;

  const submit = async () => {
    if (!canSubmit || !order?.id) return;
    // Re-check every step: the stepper lets the operator go back and edit, so
    // a step passed earlier may no longer be valid.
    const firstInvalid = [0, 1, 2, 3, 4, 5].find((index) => !stepValid(index));
    if (firstInvalid !== undefined) {
      setRevealed((prev) => ({ ...prev, [firstInvalid]: true }));
      setStep(firstInvalid);
      showToast(`Fix the highlighted fields in ${steps[firstInvalid]}.`, 'error');
      return;
    }
    setSubmitting(true);
    setError('');
    setServerErrors({});
    try {
      const body = {
        type: leg,
        charge_bearer: chargeBearer,
        line_items: selectedLines,
        package: {
          weight_g: Number(pkg.weight_g),
          length_cm: Number(pkg.length_cm),
          breadth_cm: Number(pkg.breadth_cm),
          height_cm: Number(pkg.height_cm),
        },
        payment_mode: paymentMode,
        invoice_value: invoiceValue || undefined,
        // Omitted when left to Prozo's own allocation.
        ...(courier ? { courier_channel: courier } : {}),
        internal_note: internalNote.trim(),
        ...(warehouseId ? { pickup_address_id: warehouseId } : {}),
        // Only send a party when it was actually edited, so the backend can
        // record it as copied-from-source rather than hand-entered.
        ...(editedSides.ship_from ? { ship_from: shipFrom } : {}),
        ...(editedSides.ship_to ? { ship_to: shipTo } : {}),
        ...(leg === REVERSE
          ? { return_reason: returnReason.trim(), qc_type: qcType }
          : {}),
      };

      const data = await generateShipmentLabel(order.id, body);
      setCreated(data);
      showToast(`AWB ${data.awb_number} created`, 'success');
    } catch (err) {
      setError(err.message || 'Label generation failed');
      // The server's field_errors use the form's own keys (ship_from.phone,
      // package.weight_g, invoice_value), so they mark fields directly. Take
      // the operator to the earliest step it refused.
      const refused = err?.fieldErrors || {};
      const keys = Object.keys(refused);
      if (keys.length) {
        setServerErrors(refused);
        const target = Math.min(...keys.map(stepForKey));
        setRevealed((prev) => ({ ...prev, [target]: true }));
        if (target < 6) setStep(target);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Both documents are merged server-side into one PDF — the same file the
  // Orders tab hands to the pickup rider — and saved, not opened in a tab.
  const downloadDocuments = async () => {
    if (!created?.shipment_id || downloadingDocs) return;
    try {
      setDownloadingDocs(true);
      const resp = await downloadShipmentDocuments(created.shipment_id);
      if (resp?.success && resp.combined_url) {
        const api = await import('../../api/api');
        await api.downloadFileFromUrl(
          resp.combined_url,
          resp.filename || `label_and_manifest_${created.awb_number}.pdf`
        );
      } else {
        showToast(resp?.message || 'Could not prepare the documents', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Could not download the documents', 'error');
    } finally {
      setDownloadingDocs(false);
    }
  };

  const startAnother = () => {
    setCreated(null);
    setOrder(null);
    setContext(null);
    setQuery('');
    resetForOrder();
  };

  // ---- renderers ---------------------------------------------------------

  const renderPartyFields = (side, party) => {
    const input = (field, label, extra = {}) => (
      <div className={fieldClass(`${side}.${field}`, extra.full ? 'gridFull' : '')}>
        <label htmlFor={`${side}-${field}`}>{label}</label>
        <input
          id={`${side}-${field}`}
          value={party[field] || ''}
          onChange={(e) => updateParty(side, field, e.target.value)}
          onBlur={touch(`${side}.${field}`)}
          {...(extra.inputProps || {})}
        />
        {errorText(`${side}.${field}`)}
      </div>
    );
    const emailRequired = side === 'ship_from' && leg === FORWARD;
    return (
      <div className="grid">
        {input('name', 'Name *', { inputProps: { placeholder: "Person's name" } })}
        {input('phone', 'Phone Number *', {
          inputProps: { inputMode: 'tel', placeholder: '10-digit mobile, e.g. 9876543210' },
        })}
        {input('email', emailRequired ? 'Email Address *' : 'Email Address', {
          inputProps: { type: 'email' },
        })}
        {input('address_line_1', 'Complete Address *', { full: true })}
        {input('address_line_2', 'Address Line 2')}
        {input('pincode', 'Pincode *', { inputProps: { inputMode: 'numeric', maxLength: 6 } })}
        {input('city', 'City *')}
        {input('state', 'State *')}
        {input('gstin', 'GSTIN', { inputProps: { maxLength: 15 } })}
      </div>
    );
  };

  const renderWarehouseStep = () => {
    const side = WAREHOUSE_SIDE[leg];
    const party = side === 'ship_from' ? shipFrom : shipTo;
    return (
      <>
        <div className="field">
          <label htmlFor="warehouse">
            {leg === FORWARD ? 'Please select pickup location' : 'Please select drop location'}
          </label>
          <select
            id="warehouse"
            value={warehouseId}
            onChange={(e) => chooseWarehouse(e.target.value)}
          >
            <option value="">Select a warehouse…</option>
            {(context?.warehouses || []).map((w) => (
              <option key={w.id} value={w.id}>
                {w.warehouse_name} — {w.address?.address_line_1}, {w.address?.city},{' '}
                {w.address?.pincode}
              </option>
            ))}
          </select>
          {(context?.warehouses || []).length === 0 && (
            <p className="helpText">
              This vendor has no saved warehouses. Enter the address below instead.
            </p>
          )}
        </div>
        <p className="sectionLabel">
          {leg === FORWARD ? 'Shipping from' : 'Returning to'} — edit if this shipment differs
        </p>
        {renderPartyFields(side, party)}
      </>
    );
  };

  const renderDetailsStep = () => (
    <>
      <div className="grid">
        <div className="field">
          <label htmlFor="paymentMode">Payment Mode</label>
          <select
            id="paymentMode"
            value={paymentMode}
            onChange={(e) => setPaymentMode(e.target.value)}
          >
            <option value="PREPAID">Prepaid</option>
            <option value="COD">COD</option>
          </select>
        </div>
        <div className={fieldClass('invoice_value')}>
          <label htmlFor="invoiceValue">Invoice Value (override)</label>
          <input
            id="invoiceValue"
            value={invoiceValue}
            placeholder={declaredValue ? `₹${declaredValue.toFixed(2)}` : 'From selected items'}
            onChange={(e) => setInvoiceValue(e.target.value)}
            onBlur={touch('invoice_value')}
            inputMode="decimal"
          />
          {errorText('invoice_value')}
          <p className="helpText">
            Leave blank to declare the value of the items selected below
            {declaredValue > 0 ? ` (₹${declaredValue.toFixed(2)})` : ''}.
            {paymentMode === 'COD' && ' This is what will be collected on delivery.'}
          </p>
        </div>
        <div className={fieldClass('chargeBearer')}>
          <label htmlFor="chargeBearer">Charge To *</label>
          <select
            id="chargeBearer"
            value={chargeBearer}
            onChange={(e) => setChargeBearer(e.target.value)}
          >
            <option value="">Select who pays…</option>
            <option value="SETTINGS">As per vendor settings</option>
            <option value="BRAND">Brand</option>
            <option value="SUSPIRE">Suspire</option>
          </select>
          {errorText('chargeBearer')}
          <p className="helpText">
            {chargeBearer === 'SETTINGS'
              ? "The vendor's shipping billing rule decides at the nightly run."
              : 'Overrides the vendor rule for this shipment only.'}
          </p>
        </div>
      </div>

      {leg === REVERSE && (
        <div className="grid" style={{ marginTop: 14 }}>
          <div className={fieldClass('returnReason', 'gridFull')}>
            <label htmlFor="rvpReason">RVP Reason *</label>
            <input
              id="rvpReason"
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              onBlur={touch('returnReason')}
              placeholder="Why is this coming back?"
            />
            {errorText('returnReason')}
          </div>
          <div className="field">
            <label htmlFor="qcType">QC Type</label>
            <select id="qcType" value={qcType} onChange={(e) => setQcType(e.target.value)}>
              <option value="NONE">No QC</option>
              <option value="DOORSTEP">Yes (Doorstep QC)</option>
            </select>
          </div>
        </div>
      )}

      <p className="sectionLabel">Items on this shipment *</p>
      {errorText('lines')}
      <div className="lineItems">
        <div className="selectAllRow">
          <input
            type="checkbox"
            id="selectAll"
            onChange={(e) => toggleAll(e.target.checked)}
            checked={
              lineItems.filter((li) => li.remaining_quantity > 0).length > 0 &&
              lineItems
                .filter((li) => li.remaining_quantity > 0)
                .every((li) => selection[li.line_item_id]?.checked)
            }
          />
          <label htmlFor="selectAll">Select all available items</label>
        </div>

        {lineItems.length === 0 && <div style={{ padding: 14 }} className="muted">No line items on this order.</div>}

        {lineItems.map((li) => {
          const picked = selection[li.line_item_id];
          const exhausted = li.remaining_quantity <= 0;
          return (
            <div
              key={li.line_item_id}
              className={`lineItem ${exhausted ? 'lineItemDisabled' : ''}`}
            >
              <input
                type="checkbox"
                aria-label={`Include ${li.product_name}`}
                disabled={exhausted}
                checked={Boolean(picked?.checked)}
                onChange={(e) => toggleLine(li, e.target.checked)}
              />
              <div>
                <div className="lineName">{li.product_name || li.sku}</div>
                <div className="lineMeta">
                  {li.sku} · ordered {li.ordered_quantity}
                  {li.cancelled_quantity > 0 && ` · ${li.cancelled_quantity} cancelled on Shopify`}
                </div>
              </div>
              <div className="field">
                <input
                  className="qtyInput"
                  type="number"
                  min="1"
                  max={li.remaining_quantity}
                  aria-label={`Quantity for ${li.product_name}`}
                  disabled={exhausted || !picked?.checked}
                  value={picked?.quantity ?? ''}
                  onChange={(e) => setLineField(li.line_item_id, 'quantity', e.target.value)}
                />
              </div>
              {leg === REVERSE && picked?.checked && (
                <div className="field reasonRow">
                  <label htmlFor={`reason-${li.line_item_id}`}>
                    Return reason for this item (optional)
                  </label>
                  <input
                    id={`reason-${li.line_item_id}`}
                    value={picked.reason || ''}
                    placeholder="Falls back to the RVP reason above"
                    onChange={(e) => setLineField(li.line_item_id, 'reason', e.target.value)}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );

  const renderPackageStep = () => {
    const volumetric = volumetricWeightGrams(pkg.length_cm, pkg.breadth_cm, pkg.height_cm);
    const actual = parseFloat(pkg.weight_g) || 0;
    const chargeable = Math.max(volumetric, actual);
    return (
    <>
      <p className="muted" style={{ marginTop: 0 }}>
        Dimensions in centimetres, weight in grams.
      </p>
      <div className="grid">
        {[
          ['weight_g', 'Weight (g) *'],
          ['length_cm', 'Length (cm) *'],
          ['breadth_cm', 'Breadth (cm) *'],
          ['height_cm', 'Height (cm) *'],
        ].map(([key, label]) => (
          <div key={key} className={fieldClass(`package.${key}`)}>
            <label htmlFor={key}>{label}</label>
            <input
              id={key}
              type="number"
              min="0"
              step="0.01"
              value={pkg[key]}
              onChange={(e) => setPkg((prev) => ({ ...prev, [key]: e.target.value }))}
              onBlur={touch(`package.${key}`)}
            />
            {errorText(`package.${key}`)}
          </div>
        ))}
      </div>

      {volumetric > 0 && (
        <div className="banner bannerWarn" style={{ marginTop: '1.25rem', marginBottom: 0 }}>
          <strong>Volumetric weight: {formatGrams(volumetric)}</strong>
          {actual > 0 && (
            <>
              {' '}· Actual {formatGrams(actual)} ·{' '}
              <strong>Chargeable {formatGrams(chargeable)}</strong>
            </>
          )}
          <div className="helpText" style={{ marginTop: '0.35rem' }}>
            Couriers bill on whichever is greater. Volumetric is (L × B × H) ÷ 5000, in kg.
          </div>
        </div>
      )}
    </>
    );
  };

  const fetchQuotes = useCallback(async () => {
    if (!order?.id) return;
    const fromPin = (leg === REVERSE ? shipFrom : shipFrom).pincode;
    const toPin = shipTo.pincode;
    if (!fromPin || !toPin) {
      setQuotesError('Both pincodes are needed before couriers can be priced.');
      return;
    }
    setQuotesLoading(true);
    setQuotesError('');
    // Clear the previous rates before the request, not after it: a stale price
    // shown next to a spinner invites picking a rate that is about to change.
    // The selection goes too, since the quote behind it is gone.
    setQuotes([]);
    setCourier('');
    setSelectedQuoteId('');
    try {
      const data = await getLabelCourierQuotes(order.id, {
        // The leg decides which carrier set Prozo quotes — a reverse pickup
        // returns reverse-capable carriers, not the forward ones.
        type: leg,
        from_pincode: fromPin,
        to_pincode: toPin,
        length_cm: Number(pkg.length_cm),
        breadth_cm: Number(pkg.breadth_cm),
        height_cm: Number(pkg.height_cm),
        weight_g: Number(pkg.weight_g),
        payment_mode: paymentMode,
      });
      setQuotes(data?.serviceable_options || []);
    } catch (err) {
      setQuotesError(err.message || 'Could not fetch couriers');
      setQuotes([]);
    } finally {
      setQuotesLoading(false);
    }
  }, [order?.id, leg, shipFrom, shipTo, pkg, paymentMode]);

  // Quote the lane as soon as the step opens — there are no recommendation
  // rules on this flow, so a courier must be chosen and the list is the step.
  useEffect(() => {
    if (step === 5 && quotes.length === 0 && !quotesLoading && !quotesError) {
      fetchQuotes();
    }
  }, [step, quotes.length, quotesLoading, quotesError, fetchQuotes]);

  const renderCourierStep = () => (
    <>
      <div className="courierHeader">
        <h3 className="courierHeading">
          Serviceable carriers — pricing
          {visibleQuotes.length ? ` (${visibleQuotes.length})` : ''}
        </h3>
        <RefreshRatesButton onClick={fetchQuotes} loading={quotesLoading} />
      </div>

      {quotesError && <div className="banner bannerError">{quotesError}</div>}

      {quotesLoading ? (
        <>
          <p className="quotesLoadingNote">Pricing the lane…</p>
          <QuotesSkeleton />
        </>
      ) : visibleQuotes.length === 0 ? (
        <div className="banner bannerWarn">
          No couriers are serviceable for this lane and package. Check the pincodes and
          dimensions, then refresh.
        </div>
      ) : (
        <div className="costCardsGrid">
          {visibleQuotes.map((q, idx) => {
            const {
              parent,
              providerId,
              serviceName,
              serviceTypePrimary,
              dispatchModeLabel,
              basePrice,
              finalPrice,
              taxAmount,
              taxPercentage,
              minTatDays,
              maxTatDays,
              paymentMode: quotePaymentMode,
              zone,
              codAvailable,
              channel,
            } = describeQuote(q);
            // A quote that maps to no bookable channel is shown but cannot be
            // picked — sending Prozo a channel it does not accept would fail at
            // creation, after the operator thought they had chosen.
            const selectable = Boolean(channel);
            const quoteId = providerId || `idx-${idx}`;
            const isSelected = selectable && selectedQuoteId === quoteId;

            return (
              <div
                key={`${parent}-${dispatchModeLabel}-${idx}`}
                className={`costCard ${isSelected ? 'costCardSelected' : ''}`}
              >
                <div className="costCardHeader">
                  <div className="providerInfo">
                    <div className="providerName">{parent}</div>
                    <div className="serviceName">{serviceName}</div>
                    {providerId && <div className="providerId">ID: {providerId}</div>}
                  </div>
                  <div className="costCardPrice">
                    <div className="finalPrice">₹{finalPrice.toFixed(2)}</div>
                    {basePrice !== finalPrice && (
                      <div className="basePrice">Base: ₹{basePrice.toFixed(2)}</div>
                    )}
                  </div>
                </div>

                <div className="costCardDetails">
                  {(serviceTypePrimary || dispatchModeLabel) && (
                    <div className="detailRow">
                      <span className="detailLabel">Service:</span>
                      <span className="detailValue">
                        {serviceTypePrimary || '—'}
                        {dispatchModeLabel ? ` · ${dispatchModeLabel}` : ''}
                      </span>
                    </div>
                  )}

                  {taxAmount > 0 && (
                    <div className="detailRow">
                      <span className="detailLabel">Tax ({taxPercentage}%):</span>
                      <span className="detailValue">₹{taxAmount.toFixed(2)}</span>
                    </div>
                  )}

                  {(minTatDays > 0 || maxTatDays > 0) && (
                    <div className="detailRow">
                      <span className="detailLabel">Delivery Time:</span>
                      <span className="detailValue highlight">
                        {minTatDays === maxTatDays
                          ? `${minTatDays} day${minTatDays > 1 ? 's' : ''}`
                          : `${minTatDays}-${maxTatDays} days`}
                      </span>
                    </div>
                  )}

                  {quotePaymentMode && (
                    <div className="detailRow">
                      <span className="detailLabel">Payment Mode:</span>
                      <span className="detailValue">{quotePaymentMode}</span>
                    </div>
                  )}

                  {zone && (
                    <div className="detailRow">
                      <span className="detailLabel">Zone:</span>
                      <span className="detailValue">{zone}</span>
                    </div>
                  )}

                  {codAvailable !== undefined && (
                    <div className="detailRow">
                      <span className="detailLabel">COD Available:</span>
                      <span className={`detailValue ${codAvailable ? 'success' : 'muted'}`}>
                        {codAvailable ? '✓ Yes' : '✗ No'}
                      </span>
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="selectProviderBtn"
                  disabled={!selectable}
                  onClick={() => {
                    setCourier(channel);
                    setSelectedQuoteId(quoteId);
                  }}
                >
                  {isSelected
                    ? '✓ Selected'
                    : selectable
                      ? 'Select This Option'
                      : 'Needs manual selection'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  const renderReview = () => {
    const from = shipFrom;
    const to = shipTo;
    return (
      <>
        <p className="muted" style={{ marginTop: 0 }}>
          Prozo allocates the courier. Check the details before creating the AWB — a label
          cannot be un-created, only cancelled.
        </p>
        <div className="grid" style={{ marginTop: 16 }}>
          <div>
            <p className="sectionLabel">Ship from</p>
            <div className="lineName">{from.name}</div>
            <div className="lineMeta">
              {from.address_line_1}, {from.city}, {from.state} {from.pincode}
              <br />
              {from.phone}
            </div>
          </div>
          <div>
            <p className="sectionLabel">Ship to</p>
            <div className="lineName">{to.name}</div>
            <div className="lineMeta">
              {to.address_line_1}, {to.city}, {to.state} {to.pincode}
              <br />
              {to.phone}
            </div>
          </div>
        </div>

        <p className="sectionLabel">Items ({selectedLines.length})</p>
        <div className="lineItems">
          {selectedLines.map((sel) => {
            const li = lineItems.find((x) => x.line_item_id === sel.line_item_id);
            return (
              <div key={sel.line_item_id} className="lineItem" style={{ gridTemplateColumns: '1fr 90px' }}>
                <div>
                  <div className="lineName">{li?.product_name || sel.line_item_id}</div>
                  {sel.return_reason && <div className="lineMeta">{sel.return_reason}</div>}
                </div>
                <div className="lineMeta">Qty {sel.quantity}</div>
              </div>
            );
          })}
        </div>

        <div className="grid" style={{ marginTop: 16 }}>
          <div>
            <p className="sectionLabel">Charge to</p>
            <div className="lineMeta">
              {chargeBearer === 'SETTINGS' ? 'As per vendor settings' : chargeBearer}
            </div>
          </div>
          <div>
            <p className="sectionLabel">Package</p>
            <div className="lineMeta">
              {pkg.weight_g} g · {pkg.length_cm}×{pkg.breadth_cm}×{pkg.height_cm} cm
              <br />
              Volumetric{' '}
              {formatGrams(
                volumetricWeightGrams(pkg.length_cm, pkg.breadth_cm, pkg.height_cm)
              )}
            </div>
          </div>
          <div>
            <p className="sectionLabel">Declared value</p>
            <div className="lineMeta">
              ₹{invoiceValue || declaredValue.toFixed(2)}
              {paymentMode === 'COD' && ' · collected on delivery'}
              {/* An override replaces the value of the selected items, so say
                  so plainly here — this is the last chance to catch a figure
                  that no longer matches what is actually in the box. */}
              {invoiceValue && (
                <>
                  <br />
                  <span className="overrideNote">
                    Manual override · selected items total ₹{declaredValue.toFixed(2)}
                  </span>
                </>
              )}
            </div>
          </div>
          <div>
            <p className="sectionLabel">Courier</p>
            <div className="lineMeta">
              {courier || 'Not selected'}
            </div>
          </div>
          {leg === REVERSE && (
            <div>
              <p className="sectionLabel">Return</p>
              <div className="lineMeta">
                {returnReason}
                {qcType === 'DOORSTEP' && ' · Doorstep QC'}
              </div>
            </div>
          )}
        </div>

        {/* Every label from this screen is an exception one, and accounts has to
            reconcile the shipping charge against a reason months later. Asked
            here, at the last step, so it is written with the whole shipment in
            view rather than guessed at the start. Never sent to the courier. */}
        <div className="noteBlock">
          <label className="sectionLabel" htmlFor="internalNote">
            Why is this label being raised? <span className="req">required</span>
          </label>
          <p className="muted noteHint">
            Internal only — for the accounts team, never sent to the courier or the
            customer.
          </p>
          <textarea
            id="internalNote"
            className={`noteInput ${noteTouched && !internalNote.trim() ? 'hasError' : ''}`}
            rows={3}
            value={internalNote}
            placeholder="e.g. Customer received the wrong size; replacement sent at Suspire's cost."
            onChange={(e) => setInternalNote(e.target.value)}
            onBlur={() => setNoteTouched(true)}
          />
          {noteTouched && !internalNote.trim() && (
            <div className="noteError">A reason is required before the label can be created.</div>
          )}
        </div>
      </>
    );
  };

  const renderOrderStep = () => (
    <>
      <p className="muted" style={{ marginTop: 0 }}>
        Start typing an order number — matches appear as you type.
      </p>
      <div className="searchWrap" ref={searchWrapRef}>
        <div className="field">
          <label htmlFor="orderSearch">Order number</label>
          <input
            id="orderSearch"
            value={query}
            onChange={handleQueryChange}
            onFocus={() => setShowDropdown(true)}
            placeholder="e.g. 49257"
            autoComplete="off"
            role="combobox"
            aria-expanded={showDropdown}
            aria-controls="orderSearchResults"
          />
        </div>

        {showDropdown && (
          <div className="dropdownMenu" id="orderSearchResults" role="listbox">
            {searching ? (
              <div className="dropdownItem dropdownItemDisabled">Searching…</div>
            ) : query.trim().length < 2 ? (
              <div className="dropdownItem dropdownItemDisabled">
                Type at least 2 characters to search
              </div>
            ) : results.length > 0 ? (
              results.map((row) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={order?.id === row.id}
                  key={row.id}
                  className="dropdownItem"
                  onClick={() => chooseOrder(row)}
                >
                  <span>
                    <strong>{highlightMatch(row.order_number, query)}</strong>
                    <span className="dropdownMeta">
                      {' '}
                      · {row.vendor?.company_name || row.vendor_name || ''}
                    </span>
                  </span>
                  <span className="dropdownMeta">{row.status}</span>
                </button>
              ))
            ) : (
              <div className="dropdownItem dropdownItemDisabled">
                No orders match “{query.trim()}”
              </div>
            )}
          </div>
        )}
      </div>

      {order && (
        <div className="selectedOrder" style={{ marginTop: '1.25rem', marginBottom: 0 }}>
          <span>
            <strong>{order.order_number}</strong>
            {context?.order?.vendor_name ? ` · ${context.order.vendor_name}` : ''}
            {context?.order?.status ? ` · ${context.order.status}` : ''}
          </span>
          <button type="button" className="btn btnGhost" onClick={startAnother}>
            Change order
          </button>
        </div>
      )}
    </>
  );

  const renderStep = () => {
    if (step === 0) return renderOrderStep();
    if (step === 1) {
      const side = CUSTOMER_SIDE[leg];
      return (
        <>
          <p className="sectionLabel">
            {leg === FORWARD
              ? "Who is the recipient? (buyer's details)"
              : 'Who is returning the order? (collected from)'}
          </p>
          {renderPartyFields(side, side === 'ship_from' ? shipFrom : shipTo)}
        </>
      );
    }
    if (step === 2) return renderWarehouseStep();
    if (step === 3) return renderDetailsStep();
    if (step === 4) return renderPackageStep();
    if (step === 5) return renderCourierStep();
    return renderReview();
  };

  // ---- shell -------------------------------------------------------------

  return (
    <div className="labelsContainer">
      <div className="labelsHeader">
        <h1 className="labelsTitle">Shipment Labels</h1>
        <p className="labelsSubtitle">Create a forward or return label for an existing order.</p>
      </div>

      <div className="legTabs" role="tablist">
        {[
          [FORWARD, 'Forward Shipment', '\u{1F69A}'],
          [REVERSE, 'Reverse Shipment', '\u{21A9}'],
        ].map(([value, label, icon]) => (
          <button
            key={value}
            role="tab"
            type="button"
            aria-selected={leg === value}
            className={`legTab ${leg === value ? 'legTabActive' : ''}`}
            onClick={() => {
              setLeg(value);
              resetForOrder();
            }}
          >
            <span className="legTabIcon" aria-hidden="true">
              {icon}
            </span>
            {label}
          </button>
        ))}
      </div>

      <div className="hint">
        <strong>How it works:</strong>{' '}
        {leg === FORWARD
          ? 'Find the order, confirm where it is going, pick the items and package it. Addresses are pre-filled from the order — editing them here affects only this shipment.'
          : 'Find the order, confirm where the parcel is collected from and which warehouse receives it, then pick the items coming back. The order itself is left untouched.'}
      </div>

      {cloneSource && (
        <div className="banner bannerInfo">
          <strong>Cloning AWB {cloneSource.awb_number}.</strong>{' '}
          Everything is pre-filled except the reason — state why this new label is needed.
          {cloneNotice && <div className="cloneNotice">{cloneNotice}</div>}
          <button
            type="button"
            className="bannerDismiss"
            onClick={() => setCloneSource(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {error && <div className="banner bannerError">{error}</div>}

      {created && (
        <div className="banner bannerSuccess">
          <strong>AWB {created.awb_number} created.</strong>{' '}
          {/* Prozo issues no label for a reverse shipment — the manifest is the
              only document there will ever be — so a missing label is not a
              document still on its way. Saying "still being prepared" there sent
              operators back to wait for something that was never coming. */}
          {!created.label_url && !created.manifest_url
            ? 'The documents are still being prepared — the AWB is saved and you can download them from the order.'
            : created.label_url && created.manifest_url
              ? 'Both documents are ready.'
              : created.manifest_url
                ? 'The manifest is ready. A reverse shipment has no separate label.'
                : 'The manifest is still being prepared — it usually lands within a minute.'}
          {created.qc_security_key && <> · QC key {created.qc_security_key}</>}
          <div className="successActions">
            {/* Offered whenever anything exists, named for what will arrive: the
                merged pair for a forward leg, the manifest alone for a reverse. */}
            {(created.label_url || created.manifest_url) && (
              <button
                type="button"
                className="btn btnPrimary"
                onClick={downloadDocuments}
                disabled={downloadingDocs}
              >
                {downloadingDocs
                  ? 'Downloading\u2026'
                  : created.label_url && created.manifest_url
                    ? 'Download Label & Manifest'
                    : created.manifest_url
                      ? 'Download Manifest'
                      : 'Download Label'}
              </button>
            )}
            <button type="button" className="btn btnGhost" onClick={startAnother}>
              Create another
            </button>
          </div>
        </div>
      )}

      {!created && (
        <div className="layout">
          <nav className="stepper" aria-label="Label steps">
            {steps.map((name, index) => (
              <button
                type="button"
                key={name}
                className={`step ${index === step ? 'stepCurrent' : ''} ${
                  index < step ? 'stepDone' : ''
                }`}
                disabled={index > step}
                onClick={() => setStep(index)}
              >
                <span className="stepIndex">{index < step ? '\u2713' : index + 1}</span>
                {name}
              </button>
            ))}
          </nav>

          <section className="panel">
            <h2 className="panelTitle">
              {step + 1}. {steps[step]}
            </h2>

            <div className="panelBody">
              {step > 0 && loadingContext ? (
                <p className="muted">Loading order…</p>
              ) : (
                renderStep()
              )}
            </div>

            <div className="actions">
              {step > 0 && (
                <button
                  type="button"
                  className="btn btnGhost"
                  onClick={() => setStep((prev) => prev - 1)}
                >
                  Back
                </button>
              )}
              {step < steps.length - 1 ? (
                <button type="button" className="btn btnPrimary" onClick={goNext}>
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btnPrimary"
                  disabled={!canSubmit}
                  onClick={submit}
                >
                  {submitting ? <span className="spinner" /> : null}
                  {submitting ? 'Creating\u2026' : 'Create label'}
                </button>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default ManageLabels;
